/**
 * Device-local wrap key for the Firebase (Google) sign-in path.
 *
 * Password parents derive their wrap key from PBKDF2(password + salt) each
 * time — the key material never persists beyond the login session. Google
 * parents have no password, so we need a per-device secret to protect the
 * family key at rest in IndexedDB.
 *
 * Design (plan 029):
 * - `crypto.subtle.generateKey({name:'AES-GCM',length:256}, false, ...)`
 *   creates a non-extractable `CryptoKey`. IndexedDB natively persists
 *   `CryptoKey` objects (they're structured-cloneable), so the key never
 *   crosses the JS boundary — a compromised extension can wrap/unwrap but
 *   can't exfiltrate the raw bytes.
 * - The family key is wrapped with AES-256-GCM (12-byte random IV,
 *   IV || ciphertext concatenated for storage). Same 256-bit strength as
 *   the password path's ChaCha20-Poly1305.
 * - Vault keys: `firebase_device_key` (CryptoKey), `firebase_wrapped_family_key`
 *   (Uint8Array).
 *
 * On a fresh device the CryptoKey is missing — the recover-with-mnemonic
 * flow generates a new one and re-wraps.
 */

import * as vault from './vault';

/**
 * Vault keys are namespaced by email (plan 031) so multiple Backstop
 * accounts on the same browser don't stomp each other's device keys.
 * Normalisation matches the CP's CITEXT email uniqueness — `.trim()
 * .toLowerCase()`.
 */
function normEmail(email: string): string {
  return email.trim().toLowerCase();
}
const DEVICE_KEY_PREFIX = 'firebase_device_key';
const WRAPPED_KEY_PREFIX = 'firebase_wrapped_family_key';

function deviceKeyName(email: string): string {
  return `${DEVICE_KEY_PREFIX}:${normEmail(email)}`;
}
function wrappedKeyName(email: string): string {
  return `${WRAPPED_KEY_PREFIX}:${normEmail(email)}`;
}

async function putCryptoKey(key: string, value: CryptoKey): Promise<void> {
  // vault.put's public signature expects Uint8Array; IndexedDB itself
  // accepts any structured-cloneable value. Cast at the boundary — the
  // underlying idb write is identical for CryptoKey.
  await vault.put(key, value as unknown as Uint8Array);
}

async function getCryptoKey(key: string): Promise<CryptoKey | null> {
  const raw = await vault.get(key);
  if (raw === null) return null;
  // The idb read returns whatever was stored — for a CryptoKey it's the
  // real object, not a byte view. Same cast at the boundary.
  return raw as unknown as CryptoKey;
}

/**
 * Generate a fresh non-extractable AES-256-GCM device key and stash it in
 * IndexedDB. Returns the CryptoKey so callers can wrap material immediately.
 * Overwrites any existing device key.
 */
export async function generateAndStoreDeviceKey(
  email: string,
): Promise<CryptoKey> {
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    /* extractable */ false,
    ['encrypt', 'decrypt'],
  );
  await putCryptoKey(deviceKeyName(email), key);
  return key;
}

/**
 * Fetch the persisted device key, if one exists. Returns null on a fresh
 * device that hasn't run signup/restore yet.
 */
export async function loadDeviceKey(email: string): Promise<CryptoKey | null> {
  return getCryptoKey(deviceKeyName(email));
}

/**
 * Wrap the 32-byte family key with the given device key. Format on disk:
 * `[12-byte IV][ciphertext + tag]`.
 */
export async function wrapFamilyKey(
  familyKey: Uint8Array,
  deviceKey: CryptoKey,
): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    deviceKey,
    familyKey as BufferSource,
  );
  const ctArr = new Uint8Array(ct);
  const out = new Uint8Array(12 + ctArr.byteLength);
  out.set(iv, 0);
  out.set(ctArr, 12);
  return out;
}

/**
 * Inverse of `wrapFamilyKey`. Throws (via WebCrypto AEAD failure) if the
 * blob is corrupt or the wrong device key is passed.
 */
export async function unwrapFamilyKey(
  wrapped: Uint8Array,
  deviceKey: CryptoKey,
): Promise<Uint8Array> {
  if (wrapped.length < 12 + 16) {
    throw new Error('wrapped family key too short');
  }
  const iv = wrapped.slice(0, 12);
  const ct = wrapped.slice(12);
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    deviceKey,
    ct as BufferSource,
  );
  return new Uint8Array(pt);
}

/**
 * High-level: on a signup or mnemonic-recovery flow, mint a fresh device
 * key, wrap the given family key with it, and persist both. Overwrites any
 * prior device-key vault entries — restore-with-mnemonic on a device that
 * had a different account previously will thus rotate the local secret.
 */
export async function storeFamilyKeyForFirebase(
  email: string,
  familyKey: Uint8Array,
): Promise<void> {
  if (familyKey.length !== 32) {
    throw new Error(
      `family key must be 32 bytes, got ${familyKey.length.toString()}`,
    );
  }
  const deviceKey = await generateAndStoreDeviceKey(email);
  const wrapped = await wrapFamilyKey(familyKey, deviceKey);
  await vault.put(wrappedKeyName(email), wrapped);
}

/**
 * Attempt to unwrap the persisted family key. Returns null if either the
 * device key or the wrapped blob is missing (fresh device). Throws on
 * decrypt failure — that's a corrupted vault, worth surfacing.
 */
export async function loadFamilyKeyForFirebase(
  email: string,
): Promise<Uint8Array | null> {
  const deviceKey = await loadDeviceKey(email);
  if (deviceKey === null) return null;
  const wrapped = await vault.get(wrappedKeyName(email));
  if (wrapped === null) return null;
  return unwrapFamilyKey(wrapped, deviceKey);
}

/**
 * Erase the Firebase-path vault entries for `email`. Used on account
 * deletion / wipe.
 */
export async function wipeFirebaseVault(email: string): Promise<void> {
  await vault.del(deviceKeyName(email));
  await vault.del(wrappedKeyName(email));
}
